import React, { useEffect, useRef, useState } from 'react';
import {
    Modal,
    TextInput,
    Select,
    SelectItem,
} from '@carbon/react';
import { IWatchfolder, TargetConfig } from './Watchfolder.model';
import { getWatchfolderEngineProfiles, getWatchfolderEngines, selectToken, selectWatchfolderEngines } from '../../../AppSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import EngineConfigForm from './EngineConfigForm';
import { FINGERPRINT_RESOURCE_ID, isFingerprintEngine } from '../fingerprinting';

type Props = {
    open: boolean;
    watchfolder: IWatchfolder
    onClose: () => void;
    onSubmit: (folder: IWatchfolder) => void;
};

export const CreateTaskModal: React.FC<Props> = ({ open, watchfolder, onClose, onSubmit }) => {
    const [formData, setFormData] = React.useState<IWatchfolder>(null);
    const [targetConfig, setTargetConfig] = React.useState<TargetConfig>({});
    const [extInput, setExtInput] = useState('');
    const extInputRef = useRef<HTMLInputElement>(null);
    const dispatch = useAppDispatch()
    const token = useAppSelector(selectToken)
    const engines = useAppSelector(selectWatchfolderEngines)

    const handleChange = (field: keyof IWatchfolder, value: any) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleTargetServiceChange = (targetService: string) => {
        handleChange('targetService', targetService);
        if (isFingerprintEngine(targetService, engines)) {
            setTargetConfig({ resourceId: FINGERPRINT_RESOURCE_ID });
        }
    };

    const addExtension = (raw: string) => {
        const normalized = raw.trim().toLowerCase().replace(/^\.+/, '');
        if (!normalized) return;
        const current = formData?.allowedExtensions ?? [];
        if (!current.includes(normalized)) {
            handleChange('allowedExtensions', [...current, normalized]);
        }
        setExtInput('');
    };

    const removeExtension = (ext: string) => {
        handleChange('allowedExtensions', (formData?.allowedExtensions ?? []).filter(e => e !== ext));
    };

    const onExtKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
            e.preventDefault();
            addExtension(extInput);
        } else if (e.key === 'Backspace' && extInput === '') {
            const exts = formData?.allowedExtensions ?? [];
            if (exts.length > 0) removeExtension(exts[exts.length - 1]);
        }
    };

    useEffect(() => {
        setFormData({ ...watchfolder })
        setTargetConfig(watchfolder?.targetConfig ?? {})
        dispatch(getWatchfolderEngines({ token }))

        return () => {
            setFormData(null)
        }
    }, [watchfolder])

    useEffect(() => {
        setFormData({
            ...formData,
            targetConfig: targetConfig
        });
    }, [targetConfig]);

    useEffect(() => {
        engines
            .filter(engine => Boolean(engine.configSchema?.profileEndpoint))
            .forEach(engine => dispatch(getWatchfolderEngineProfiles({ token, engine })))

        if (isFingerprintEngine(formData?.targetService, engines)) {
            setTargetConfig({ resourceId: FINGERPRINT_RESOURCE_ID });
        }
    }, [engines])

    const handleSubmit = () => {
        onSubmit(formData);
        onClose();
    };

    if (!formData) return null

    return (
        <Modal
            open={open}
            modalHeading={formData?.id === "new_0" ? "Create New Watchfolder" : "Edit Watchfolder"}
            primaryButtonText={formData?.id === "new_0" ? "Create" : "Update"}
            secondaryButtonText="Cancel"
            onRequestClose={onClose}
            onRequestSubmit={handleSubmit}
            preventCloseOnClickOutside={true}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <TextInput
                    id="folder-name"
                    labelText="Name"
                    value={formData.label ?? ""}
                    onChange={(e) => handleChange('label', e.target.value)}
                />

                <TextInput
                    id="folder-path"
                    labelText="Watchfolder Path"
                    value={formData.path ?? ""}
                    onChange={(e) => handleChange('path', e.target.value)}
                />

                <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: '#c6c6c6' }}>Allowed Extensions</div>
                    <div
                        style={{
                            display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                            border: '1px solid #6f6f6f', padding: '6px 8px', minHeight: 40,
                            background: '#262626', cursor: 'text',
                        }}
                        onClick={() => extInputRef.current?.focus()}
                    >
                        {(formData.allowedExtensions ?? []).map(ext => (
                            <span key={ext} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: '#525252', color: '#f4f4f4', padding: '2px 8px', fontSize: 12,
                            }}>
                                .{ext}
                                <span
                                    style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: 13, lineHeight: 1 }}
                                    onClick={(e) => { e.stopPropagation(); removeExtension(ext); }}
                                >×</span>
                            </span>
                        ))}
                        <input
                            ref={extInputRef}
                            value={extInput}
                            onChange={e => setExtInput(e.target.value)}
                            onKeyDown={onExtKeyDown}
                            onBlur={() => addExtension(extInput)}
                            placeholder={(formData.allowedExtensions ?? []).length === 0 ? 'e.g. mp4 mxf' : ''}
                            style={{
                                border: 'none', outline: 'none', flex: 1, minWidth: 80,
                                fontSize: 14, background: 'transparent', color: '#f4f4f4',
                            }}
                        />
                    </div>
                    <div style={{ fontSize: 11, color: '#a8a8a8', marginTop: 3 }}>Press Space, Enter, or comma to add. Backspace to remove last.</div>
                </div>

                <Select
                    id="task-system"
                    labelText="Action"
                    defaultValue={formData.targetService?.toUpperCase()}
                    value={formData.targetService?.toUpperCase()}
                    onChange={(e) => handleTargetServiceChange(e.target.value)}
                >
                    {engines?.length && engines.map(engine => <SelectItem key={engine?.id} text={engine?.label} value={engine?.id} />)}
                </Select>

                <EngineConfigForm selectedEngine={formData.targetService?.toUpperCase()} formData={targetConfig} setFormData={setTargetConfig} />
            </div>
        </Modal>
    );
};
